import * as angular from 'angular';
import * as moment from 'pkp-moment-timezone-all-years';
import { DateTimePickerViewValue } from '../custom';
import { UtilService } from 'app/_services/util.service';

export class PickerService {
    public readonly DATE_TIME_FORMAT: 'YYYY-MM-DDTHH:mmZ' = 'YYYY-MM-DDTHH:mmZ';

    public maxDate: Date;
    public gasDayEffectiveRuntimePreSelection: DateInterval;

    constructor(private utilService: UtilService) {
        this.maxDate = new Date(9999, 11, 31, 23, 59, 0, 0);
        this.gasDayEffectiveRuntimePreSelection =
            this.utilService.getRunTimeSuggestion('DAY');
    }

    /**
     * Convert to CET, but leave the local timezone information in place.
     * {@link asCet} on the other hand will only change the timezone information of a <tt>Date</tt>,
     * which is the exact opposite of this method.
     * It makes sense when you're already receiving something in CET and the
     * {@link angular.INgModelController.$parsers}---namely {@link parseViewValue}---would then change the already correct result.
     * For instance, {@link App.TimePickerComponentCtrl}'s time is already in CET, so you'd want to first remove the offset
     * using this method so the parser {@link parseViewValue} can do its job.
     * As this method and {@link asCet} are called in sequence, you'll basically end up having done no changes to the <tt>Date</tt> instance.
     *
     * @example
     * var x = new Date();
     * x.toString() // "Thu Apr 20 2017 01:52:05 GMT+0300 (MSK)"
     * this.fakeToBeCet(x).toString() // "Thu Apr 20 2017 00:52:05 GMT+0300 (MSK)"
     *
     * @returns The given date with its hours and minutes part converted to CET.
     */
    private addCetOffset(date: Date): Date {
        const differenceBetweenLocalAndCetInMinutes =
            (this.utilService.isCentralEuropeanSummerTime(date) ? 120 : 60) +
            date.getTimezoneOffset();
        return moment(date)
            .add({ minutes: differenceBetweenLocalAndCetInMinutes })
            .toDate();
    }

    /**
     * Combine the date part of one <tt>Date</tt>. object with the time part of another to a CET string.
     * Primitive string concatenation with appended CET timezone information.
     *
     * This can be understood as the parser of the user's input. It's like we pretend the user has entered a CET,
     * although it's in fact his local time.
     *
     * @example
     * x.toString() // "Thu Apr 20 2017 01:52:05 GMT+0300 (MSK)"
     * this.toCetString(x, x) // "2017-04-20T01:52+02:00"
     */
    public asCet(date: Date, time: Date): string {
        return (
            date.getFullYear() +
            '-' +
            this.utilService.padLeadingZero(date.getMonth() + 1) +
            '-' +
            this.utilService.padLeadingZero(date.getDate()) +
            'T' +
            this.utilService.padLeadingZero(time.getHours()) +
            ':' +
            this.utilService.padLeadingZero(time.getMinutes()) +
            // TODO 1. We cannot just assume that everybody is using their browser in CET land. We need timezone information here!
            // TODO 2. In case a user selects the clock change day at 2:00, we need to know which one they mean.
            // TODO 3. The following line has been only half-fixed (hotfix for PROD)! It cannot be really fixed until 1 and 2 are fixed.
            (this.utilService.isCentralEuropeanSummerTime(
                moment(date).hour(time.getHours()).toDate()
            )
                ? '+02:00'
                : '+01:00')
        );
    }

    public preSet(
        timeOrDate: 'time' | 'date',
        fallback: Date,
        preSetDayAhead: boolean,
        modelValueToPreSetTo?: Date | string
    ): Date {
        let result: Date; // TODO result as a var name sucks

        if (modelValueToPreSetTo) {
            result = this.addCetOffset(
                new Date(modelValueToPreSetTo as string)
            );
        } else if (preSetDayAhead) {
            if (timeOrDate === 'date') {
                result = undefined;
            } else {
                result = this.addCetOffset(
                    <Date>this.gasDayEffectiveRuntimePreSelection.start
                );
            }
        } else {
            result = angular.copy(fallback);
        }

        return result;
    }

    public onTimePickerModelChange(
        timePickerViewValue: Date | 'now' | 'infinity'
    ): Date {
        let result: Date; // TODO result as a var name sucks

        if (timePickerViewValue === 'now') {
            result = this.addCetOffset(new Date());
        } else if (timePickerViewValue === 'infinity') {
            result = this.maxDate;
        } else {
            result = timePickerViewValue;
        }

        return result;
    }

    public parseViewValue(viewValue: DateTimePickerViewValue): Date {
        if (viewValue.date == null || viewValue.time == null) {
            return undefined;
        }

        try {
            const cetString = this.asCet(viewValue.date, viewValue.time);

            if (!moment(cetString, this.DATE_TIME_FORMAT).isValid()) {
                return undefined;
            }

            return <Date>new Date(cetString);
        } catch (e) {
            return undefined;
        }
    }
}

angular.module('app').service('pickerService', PickerService);
